import { Store } from 'vuex';
import { ProxyApi, ProxyAuthCloudCredential, ProxyAuthToken, ProxyRequestOptions } from '@shell/apis/intf/shell';
import { Metadata } from '@shell/types/resources/settings';
import { MANAGEMENT } from '@shell/config/types';
import { get } from '@shell/utils/object';

/**
 * Shell implementation of the `ProxyApi`.
 *
 * This API centralizes proxy request behavior used across providers:
 * - building `/meta/proxy` URLs
 * - applying credential or token auth headers
 * - dispatching requests via `management/request`
 * - optional de-pagination for list endpoints
 */
export class ProxyApiImpl implements ProxyApi {
  private store: Store<any>;

  /**
   * Creates a new Proxy API instance.
   *
   * @param store - Root Vuex store used to dispatch `management/request`.
   */
  constructor(store: Store<any>) {
    this.store = store;
  }

  /**
   * Executes a generic proxy request.
   *
   * Behavior:
   * - Builds a proxied URL and dispatches `management/request`.
   * - When `dePaginate` is enabled, follows the configured `next` URL and
   *   merges results recursively.
   *
   * @param options - Proxy request options.
   * @returns The response payload or accumulated de-paginated output.
   */
  public async request(options: ProxyRequestOptions): Promise<any> {
    const url = this.buildProxyUrl(options);
    const headers = this.buildHeaders(options);
    const req = {
      url,
      method:               options.method,
      headers,
      redirectUnauthorized: false,
    } as any;

    if (options.data !== undefined) {
      req.data = options.data;
    }

    const res = await this.store.dispatch('management/request', req, { root: true });

    if (options.postProcess) {
      return options.postProcess(res);
    }

    return res;
  }

  /**
   * Builds a `/meta/proxy` URL by stripping the scheme from `options.url`.
   */
  private buildProxyUrl(options: ProxyRequestOptions): string {
    const raw = options.url.toString();

    return `/meta/proxy/${ raw.replace(/^https:\/\//, '').replace(/^http:\/\//, 'http:/') }`;
  }

  /**
   * Builds request headers, including auth headers derived from `options.authentication`.
   */
  private buildHeaders(options: ProxyRequestOptions): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: options.accept || 'application/json',
      ...(options.headers || {})
    };

    const auth = options.authentication;

    if (!auth) {
      return headers;
    }

    if ('id' in auth) {
      // Cloud credential — instruct Rancher to sign the request server-side
      const {
        id, authSigner, usernameField, passwordField
      } = auth as ProxyAuthCloudCredential;
      let value = authSigner ? `${ authSigner } ` : '';

      value += `credID=${ id } `;
      if (usernameField) {
        value += `usernameField=${ usernameField } `;
      }
      if (passwordField) {
        value += `passwordField=${ passwordField } `;
      }
      headers['x-api-cattleauth-header'] = value.trimEnd();
    } else {
      // Plain token — forwarded verbatim as Authorization by the proxy
      const { token, authSigner } = auth as ProxyAuthToken;
      const scheme = authSigner ?? 'Bearer';

      headers['x-api-auth-header'] = `${ scheme } ${ token }`;
    }

    return headers;
  }

  /**
   * Builds the proxy URL and auth headers for a request without dispatching it.
   *
   * Useful for integrating with lower-level HTTP handlers (e.g. the AWS SDK
   * request handler) that need to apply proxy routing and auth themselves
   * rather than going through the Vuex `management/request` action.
   *
   * @param options - Proxy request options.
   * @returns An object containing the resolved proxy `url` and the `headers`
   *          to merge into the outbound request.
   */
  public prepareRequest(options: ProxyRequestOptions): { url: string; headers: Record<string, string> } {
    return {
      url:     this.buildProxyUrl(options),
      headers: this.buildHeaders(options),
    };
  }

  /**
   * Creates a `ProxyEndpoint` CR that registers the given domain patterns
   * with the Rancher `/meta/proxy` allow-list.
   *
   * The CR is cluster-scoped (`management.cattle.io/v3`, kind `ProxyEndpoint`).
   * Each entry in `urls` maps to a `spec.routes[].domain` entry on the CR.
   *
   * @param domains - Bare hostnames or wildcard patterns accepted by the proxy
   *                  backend (e.g. `api.example.com`, `%.amazonaws.com`).
   * @param name    - Optional metadata name for the CR. When omitted a name is
   *                  auto-generated by the API server.
   * @returns The created `ProxyEndpoint` resource.
   */
  public async allowDomains(domains: string[], name?: string): Promise<any> {
    const metadata = {} as Metadata;

    if (name) {
      metadata.name = name;
    } else {
      metadata.generateName = 'endpoints-';
    }
    const resource = await this.store.dispatch('management/create', {
      type: MANAGEMENT.PROXY_ENDPOINT,
      metadata,
      spec: { routes: domains.map((domain) => ({ domain })) },
    });

    return resource.save();
  }

  /**
   * Checks whether a domain pattern is already present in any existing
   * `ProxyEndpoint` CR.
   *
   * The comparison is a case-insensitive exact match against each
   * `spec.routes[].domain` entry across all CRs — the same format accepted
   * by `allowDomains` (e.g. `api.example.com`, `%.amazonaws.com`).
   *
   * @param domain - The hostname or wildcard pattern to look up.
   * @returns `true` if the pattern is found in at least one `ProxyEndpoint` CR,
   *          `false` otherwise.
   */
  public async isDomainAllowed(domain: string): Promise<boolean> {
    const endpoints: any[] = await this.store.dispatch('management/findAll', { type: MANAGEMENT.PROXY_ENDPOINT });

    const needle = domain.toLowerCase();

    return endpoints.some((endpoint) => (endpoint?.spec?.routes ?? []).some(
      (route: { domain: string }) => route.domain?.toLowerCase() === needle
    )
    );
  }

  /**
   * Checks whether a `ProxyEndpoint` CR with the given name exists.
   *
   * Uses `management/find` and treats a 404 response as `false` rather than
   * an error. Any other error is re-thrown.
   *
   * @param name - The metadata name of the `ProxyEndpoint` CR to look up.
   * @returns `true` if the CR exists, `false` if it is not found.
   */
  public async hasProxyEndpoint(name: string): Promise<boolean> {
    try {
      await this.store.dispatch('management/find', {
        type: MANAGEMENT.PROXY_ENDPOINT,
        id:   name,
        opt:  { force: false },
      });

      return true;
    } catch (e: any) {
      if (e?.status === 404) {
        return false;
      }
      throw e;
    }
  }
}

/**
 * Creates a `postProcess` callback for use with `ProxyApi.request()` that
 * automatically follows pagination links and merges array results across pages.
 *
 * @param proxyApi       - The `ProxyApi` instance used to fetch subsequent pages.
 * @param requestOptions - Base request options (without `postProcess`) reused for each page.
 * @param opts.nextUrlPath - Dot-path or jsonpath to the next-page URL in the response.
 *                           Defaults to `links.pages.next`.
 * @param opts.mergeKey    - Response key whose array value is merged across pages.
 *                           When omitted the first top-level array key is used.
 *
 * @example
 * ```ts
 * const baseOptions = { url: 'api.example.com/v1/items', authentication };
 * const result = await proxy.request({
 *   ...baseOptions,
 *   postProcess: createDepaginator(proxy, baseOptions, { mergeKey: 'items' }),
 * });
 * ```
 */
export function createDepaginator(
  proxyApi: ProxyApi,
  requestOptions: Omit<ProxyRequestOptions, 'postProcess'>,
  opts: { nextUrlPath?: string; mergeKey?: string } = {}
): (res: any) => Promise<any> {
  const nextPath = opts.nextUrlPath ?? 'links.pages.next';
  const mergeKey = opts.mergeKey;

  const paginate = async(res: any, accumulated?: any): Promise<any> => {
    const key = mergeKey ?? Object.keys(res ?? {}).find((k) => Array.isArray(res[k]));

    // If we have a prior page's data, concatenate this page's array into it; otherwise use this page as-is
    const merged = (accumulated !== undefined && key && Array.isArray(accumulated[key]) && Array.isArray(res?.[key])) ? { ...res, [key]: [...accumulated[key], ...res[key]] } : res;

    const nextUrl = get(merged, nextPath);

    if (!nextUrl) {
      return merged;
    }

    const nextRes = await proxyApi.request({ ...requestOptions, url: new URL(nextUrl) });

    return paginate(nextRes, merged);
  };

  return (res: any) => paginate(res);
}
